import * as React from "react";
import { Box, Stack, Typography, Button, IconButton, CircularProgress, Tooltip } from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import DragIndicatorRoundedIcon from "@mui/icons-material/DragIndicatorRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import {
  deleteMissionRisk,
  fetchMissionRisks,
  insertMissionRisk,
  updateMissionRisk,
} from "../../../api/queries";
import { createPendingEdits, subscribeToRows, upsertById } from "../../../api/sheetSync";
import { RISK_IMPACTS, RISK_LIKELIHOODS, makeCellId } from "../../../types/risk";
import type { MissionRiskRow, RiskMatrixCell } from "../../../types/risk";
import { IMPACT_LABELS, LIKELIHOOD_LABELS, bandFor, BAND_COLORS, BAND_LABELS, RISK_BANDS } from "../riskMatrixTheme";
import MitigationTable from "./MitigationTable";
import { AuthorTip } from "../sheetAuthors";

const MONO = "'Roboto Mono', 'Courier New', monospace";
const DRAG_MIME = "application/x-risk-id";

// Plain inline style, not MUI's sx -- "backgroundColor", not "bgcolor"
// (the latter is only meaningful inside sx and is silently dropped here,
// which is what left these inputs on the browser's default white
// background with white text on top of it, invisible while typing).
const fieldSx = {
  width: "100%",
  boxSizing: "border-box" as const,
  backgroundColor: "rgba(9,5,11,0.55)",
  border: "1px solid rgba(255,255,255,0.16)",
  borderRadius: 8,
  color: "#FFFFFF",
  fontFamily: MONO,
  fontSize: 13,
  padding: "9px 12px",
  outline: "none",
};

function TextCell(props: { value: string; placeholder: string; onChange: (value: string) => void }) {
  const [text, setText] = React.useState(props.value);

  React.useEffect(() => setText(props.value), [props.value]);

  return (
    <input
      type="text"
      placeholder={props.placeholder}
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        props.onChange(e.target.value);
      }}
      style={fieldSx as React.CSSProperties}
      onFocus={(e) => (e.currentTarget.style.borderColor = "#A78BFA")}
      onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.16)")}
    />
  );
}

// The small draggable "ID" token -- used both in a register row (source,
// when unplaced) and inside a matrix cell (source + destination, so a
// placed risk can be dragged straight to a different cell).
function RiskChip(props: { risk: MissionRiskRow; onDragStart: () => void; onDragEnd: () => void; tone?: "light" | "dark" }) {
  const { risk, tone = "light" } = props;

  return (
    <Tooltip title={risk.description || "No description"} arrow>
      <Box
        draggable
        onDragStart={(e) => {
          e.dataTransfer.setData(DRAG_MIME, risk.id);
          e.dataTransfer.setData("text/plain", risk.id);
          e.dataTransfer.effectAllowed = "move";
          props.onDragStart();
        }}
        onDragEnd={props.onDragEnd}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: { xs: 0.15, sm: 0.4 },
          px: { xs: 0.5, sm: 1 },
          py: { xs: 0.2, sm: 0.4 },
          maxWidth: "100%",
          borderRadius: 1.5,
          cursor: "grab",
          userSelect: "none",
          fontFamily: MONO,
          fontWeight: 800,
          fontSize: { xs: 10, sm: 12.5 },
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          color: tone === "light" ? "#E9D5FF" : "#FFFFFF",
          bgcolor: tone === "light" ? "rgba(168,85,247,0.18)" : "rgba(0,0,0,0.18)",
          border: tone === "light" ? "1px solid rgba(168,85,247,0.4)" : "1px solid rgba(255,255,255,0.35)",
          "&:active": { cursor: "grabbing" },
        }}
      >
        <DragIndicatorRoundedIcon sx={{ fontSize: { xs: 11, sm: 14 }, opacity: 0.7, display: { xs: "none", sm: "inline-flex" } }} />
        {risk.risk_code || "?"}
      </Box>
    </Tooltip>
  );
}

export default function RiskMatrix(props: { groupId: string }) {
  const { groupId } = props;
  const [risks, setRisks] = React.useState<MissionRiskRow[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [adding, setAdding] = React.useState(false);
  const [, setDraggingId] = React.useState<string | null>(null);
  const [dragOverCell, setDragOverCell] = React.useState<RiskMatrixCell | null>(null);
  const saveTimers = React.useRef<Record<string, number>>({});
  const pending = React.useRef(createPendingEdits());

  // Loads the group's risks, then keeps them in step with the other
  // members' edits. Fields this browser is still editing keep their local
  // value (see createPendingEdits).
  React.useEffect(() => {
    let mounted = true;
    setLoading(true);

    const load = () =>
      fetchMissionRisks(groupId).then(({ data, error }) => {
        if (!mounted) return;
        setLoading(false);
        if (error) return;
        setRisks((data ?? []).map((risk) => pending.current.merge(risk.id, risk)));
      });

    const unsubscribe = subscribeToRows<MissionRiskRow>(
      "mission_risks",
      `group_id=eq.${groupId}`,
      (change) => {
        if (!mounted) return;
        if (change.type === "DELETE") {
          setRisks((prev) => prev.filter((r) => r.id !== change.id));
          return;
        }
        const risk = pending.current.merge(change.row.id, change.row);
        setRisks((prev) => upsertById(prev, risk));
      },
      load
    );
    load();

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [groupId]);

  const setRisk = (id: string, updater: (risk: MissionRiskRow) => MissionRiskRow) => {
    setRisks((prev) => prev.map((r) => (r.id === id ? updater(r) : r)));
  };

  const save = (id: string, patch: Partial<Pick<MissionRiskRow, "risk_code" | "description" | "matrix_cell">>) => {
    pending.current.mark(id, patch);
    updateMissionRisk(id, patch).then(({ error }) => {
      if (error) console.error("Failed to save risk:", error.message);
    });
  };

  const changeCode = (id: string, value: string) => {
    setRisk(id, (r) => ({ ...r, risk_code: value }));
    pending.current.mark(id, { risk_code: value });
    window.clearTimeout(saveTimers.current[`${id}-code`]);
    saveTimers.current[`${id}-code`] = window.setTimeout(() => save(id, { risk_code: value }), 500);
  };

  const changeDescription = (id: string, value: string) => {
    setRisk(id, (r) => ({ ...r, description: value }));
    pending.current.mark(id, { description: value });
    window.clearTimeout(saveTimers.current[`${id}-desc`]);
    saveTimers.current[`${id}-desc`] = window.setTimeout(() => save(id, { description: value }), 500);
  };

  const placeOnCell = (id: string, cell: RiskMatrixCell) => {
    setRisk(id, (r) => ({ ...r, matrix_cell: cell }));
    save(id, { matrix_cell: cell });
  };

  const unplace = (id: string) => {
    setRisk(id, (r) => ({ ...r, matrix_cell: null }));
    save(id, { matrix_cell: null });
  };

  const addRisk = async () => {
    setAdding(true);
    const { data, error } = await insertMissionRisk(groupId, risks.length);
    setAdding(false);
    if (error || !data) return;
    setRisks((prev) => upsertById(prev, data));
  };

  const removeRisk = (id: string) => {
    setRisks((prev) => prev.filter((r) => r.id !== id));
    pending.current.forget(id);
    Object.keys(saveTimers.current)
      .filter((k) => k.startsWith(`${id}-`))
      .forEach((k) => window.clearTimeout(saveTimers.current[k]));
    deleteMissionRisk(id).then(({ error }) => {
      if (error) console.error("Failed to delete risk:", error.message);
    });
  };

  const risksByCell = React.useMemo(() => {
    const map = new Map<RiskMatrixCell, MissionRiskRow[]>();
    for (const risk of risks) {
      if (!risk.matrix_cell) continue;
      const list = map.get(risk.matrix_cell) ?? [];
      list.push(risk);
      map.set(risk.matrix_cell, list);
    }
    return map;
  }, [risks]);

  const unplacedRisks = risks.filter((r) => !r.matrix_cell);

  return (
    <Stack spacing={2.5}>
      <Stack spacing={1}>
        <Typography sx={{ color: "#FFFFFF", fontWeight: 800, fontSize: { xs: 16, sm: 17.5 } }}>Risk register</Typography>
        <Typography sx={{ color: "rgba(226,232,240,0.55)", fontSize: 12.5 }}>
          Drag a risk's ID onto the matrix below to place it.
        </Typography>
      </Stack>

      {loading ? (
        <Stack alignItems="center" sx={{ py: 4 }}>
          <CircularProgress size={22} sx={{ color: "#C4B5FD" }} />
        </Stack>
      ) : (
        <Stack spacing={1.25}>
          {risks.map((risk) => (
            <Box
              key={risk.id}
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "110px 1fr auto auto" },
                gap: 1.25,
                alignItems: "center",
                p: 1.25,
                borderRadius: 2,
                border: "1px solid rgba(168,85,247,0.22)",
                bgcolor: "rgba(255,255,255,0.03)",
              }}
            >
              <AuthorTip editedBy={risk.edited_by} field="risk_code">
                <TextCell value={risk.risk_code} placeholder="ID" onChange={(v) => changeCode(risk.id, v)} />
              </AuthorTip>
              <AuthorTip editedBy={risk.edited_by} field="description">
                <TextCell value={risk.description} placeholder="Description" onChange={(v) => changeDescription(risk.id, v)} />
              </AuthorTip>

              <Box sx={{ minWidth: { sm: 150 }, display: "flex", justifyContent: { xs: "flex-start", sm: "flex-end" } }}>
                {risk.matrix_cell ? (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography sx={{ fontFamily: MONO, fontSize: 11, color: "rgba(226,232,240,0.7)" }}>
                      {IMPACT_LABELS[risk.matrix_cell.split("__")[0] as keyof typeof IMPACT_LABELS]} ·{" "}
                      {LIKELIHOOD_LABELS[risk.matrix_cell.split("__")[1] as keyof typeof LIKELIHOOD_LABELS]}
                    </Typography>
                    <IconButton size="small" onClick={() => unplace(risk.id)} aria-label="Remove from matrix">
                      <CloseRoundedIcon sx={{ fontSize: 15, color: "rgba(226,232,240,0.6)" }} />
                    </IconButton>
                  </Stack>
                ) : (
                  <RiskChip risk={risk} onDragStart={() => setDraggingId(risk.id)} onDragEnd={() => setDraggingId(null)} />
                )}
              </Box>

              <IconButton size="small" onClick={() => removeRisk(risk.id)} aria-label="Delete risk">
                <DeleteOutlineRoundedIcon sx={{ fontSize: 17, color: "rgba(248,113,113,0.75)" }} />
              </IconButton>
            </Box>
          ))}

          <Button
            size="small"
            onClick={addRisk}
            disabled={adding}
            startIcon={adding ? <CircularProgress size={14} sx={{ color: "inherit" }} /> : <AddRoundedIcon sx={{ fontSize: 16 }} />}
            sx={{
              alignSelf: "flex-start",
              textTransform: "none",
              fontWeight: 800,
              fontSize: 12.5,
              borderRadius: 1.5,
              color: "#FFFFFF",
              bgcolor: "#7C3AED",
              px: 1.5,
              "&:hover": { bgcolor: "#6D28D9" },
            }}
          >
            Add risk
          </Button>
        </Stack>
      )}

      {!loading && (
        <Stack spacing={1.5}>
          <Typography sx={{ color: "#FFFFFF", fontWeight: 800, fontSize: { xs: 16, sm: 17.5 } }}>Risk matrix</Typography>

          <Box
            sx={{
              display: "grid",
              // Two narrow header columns/rows (axis name + level name) then
              // the 5 data columns -- explicit grid-column/row placement
              // below merges the axis-name cells across all data rows/cols,
              // matching a spreadsheet-style matrix with merged headers.
              gridTemplateColumns: {
                xs: "16px 76px repeat(5, minmax(0,1fr))",
                sm: "22px 130px repeat(5, minmax(0,1fr))",
                md: "26px 150px repeat(5, minmax(0,1fr))",
              },
              gridTemplateRows: {
                xs: "20px 30px repeat(6, minmax(30px, auto))",
                sm: "24px 36px repeat(6, minmax(34px, auto))",
              },
              width: "100%",
              border: "1px solid rgba(255,255,255,0.16)",
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            {/* corner: spans both header columns and both header rows */}
            <Box
              sx={{
                gridColumn: "1 / 3",
                gridRow: "1 / 3",
                bgcolor: "#171325",
                borderRight: "1px solid rgba(255,255,255,0.16)",
                borderBottom: "1px solid rgba(255,255,255,0.16)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 0.5,
              }}
            >
              <Typography
                sx={{ color: "rgba(226,232,240,0.6)", fontWeight: 800, fontSize: { xs: 8.5, sm: 10.5 }, textAlign: "center" }}
              >
                Risk Matrix
              </Typography>
            </Box>

            {/* "Likelihood" axis label, merged across the 5 data columns */}
            <Box
              sx={{
                gridColumn: "3 / 8",
                gridRow: "1 / 2",
                bgcolor: "#1E1B2E",
                borderBottom: "1px solid rgba(255,255,255,0.16)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography sx={{ color: "#E9D5FF", fontWeight: 800, fontSize: { xs: 10, sm: 12.5 }, letterSpacing: "0.04em" }}>
                Likelihood
              </Typography>
            </Box>

            {/* likelihood level names, one per data column */}
            {RISK_LIKELIHOODS.map((likelihood, index) => (
              <Box
                key={likelihood}
                sx={{
                  gridColumn: `${3 + index} / ${4 + index}`,
                  gridRow: "2 / 3",
                  minWidth: 0,
                  bgcolor: "#252036",
                  borderRight: index < RISK_LIKELIHOODS.length - 1 ? "1px solid rgba(255,255,255,0.16)" : "none",
                  borderBottom: "1px solid rgba(255,255,255,0.16)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  px: { xs: 0.25, sm: 0.75 },
                }}
              >
                <Typography
                  sx={{
                    color: "rgba(226,232,240,0.92)",
                    fontWeight: 800,
                    fontSize: { xs: 8, sm: 11, md: 12 },
                    lineHeight: 1.15,
                    overflowWrap: "break-word",
                  }}
                >
                  {LIKELIHOOD_LABELS[likelihood]}
                </Typography>
              </Box>
            ))}

            {/* "Impact" axis label, merged down all 6 data rows */}
            <Box
              sx={{
                gridColumn: "1 / 2",
                gridRow: "3 / 9",
                bgcolor: "#1E1B2E",
                borderRight: "1px solid rgba(255,255,255,0.16)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                sx={{
                  color: "#E9D5FF",
                  fontWeight: 800,
                  fontSize: { xs: 9, sm: 12.5 },
                  letterSpacing: "0.04em",
                  writingMode: "vertical-rl",
                  transform: "rotate(180deg)",
                  whiteSpace: "nowrap",
                }}
              >
                Impact
              </Typography>
            </Box>

            {RISK_IMPACTS.map((impact, rowIndex) => (
              <React.Fragment key={impact}>
                {/* impact level name for this row */}
                <Box
                  sx={{
                    gridColumn: "2 / 3",
                    gridRow: `${3 + rowIndex} / ${4 + rowIndex}`,
                    minWidth: 0,
                    display: "flex",
                    alignItems: "center",
                    px: { xs: 0.5, sm: 1.25 },
                    bgcolor: "#252036",
                    borderRight: "1px solid rgba(255,255,255,0.16)",
                    borderBottom: rowIndex < RISK_IMPACTS.length - 1 ? "1px solid rgba(255,255,255,0.16)" : "none",
                  }}
                >
                  <Typography
                    sx={{
                      color: "#E9D5FF",
                      fontWeight: 800,
                      fontSize: { xs: 9, sm: 12, md: 13 },
                      lineHeight: 1.15,
                      overflowWrap: "break-word",
                    }}
                  >
                    {IMPACT_LABELS[impact]}
                  </Typography>
                </Box>

                {RISK_LIKELIHOODS.map((likelihood, colIndex) => {
                  const band = BAND_COLORS[bandFor(impact, colIndex)];
                  const cellId = makeCellId(impact, likelihood);
                  const cellRisks = risksByCell.get(cellId) ?? [];
                  const isDragOver = dragOverCell === cellId;
                  const isLastCol = colIndex === RISK_LIKELIHOODS.length - 1;
                  const isLastRow = rowIndex === RISK_IMPACTS.length - 1;

                  return (
                    <Box
                      key={likelihood}
                      onDragOver={(e) => {
                        // Must preventDefault on every dragover for the
                        // browser to treat this as a valid drop target --
                        // gating this on React state (which updates async)
                        // is what made drops unreliable before.
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "move";
                        if (dragOverCell !== cellId) setDragOverCell(cellId);
                      }}
                      onDragLeave={() => setDragOverCell((prev) => (prev === cellId ? null : prev))}
                      onDrop={(e) => {
                        e.preventDefault();
                        setDragOverCell(null);
                        setDraggingId(null);
                        const riskId = e.dataTransfer.getData(DRAG_MIME) || e.dataTransfer.getData("text/plain");
                        if (riskId) placeOnCell(riskId, cellId);
                      }}
                      sx={{
                        gridColumn: `${3 + colIndex} / ${4 + colIndex}`,
                        gridRow: `${3 + rowIndex} / ${4 + rowIndex}`,
                        minWidth: 0,
                        minHeight: { xs: 30, sm: 34 },
                        p: { xs: 0.3, sm: 0.6 },
                        bgcolor: isDragOver ? band.fillHover : band.fill,
                        outline: isDragOver ? "3px solid #FFFFFF" : "none",
                        outlineOffset: "-3px",
                        borderRight: isLastCol ? "none" : "1px solid rgba(255,255,255,0.22)",
                        borderBottom: isLastRow ? "none" : "1px solid rgba(255,255,255,0.22)",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.25,
                        transition: "background-color 0.12s ease",
                      }}
                    >
                      {cellRisks.length > 0 && (
                        <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 0.3 }}>
                          {cellRisks.map((risk) => (
                            <RiskChip
                              key={risk.id}
                              risk={risk}
                              tone="dark"
                              onDragStart={() => setDraggingId(risk.id)}
                              onDragEnd={() => setDraggingId(null)}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </React.Fragment>
            ))}
          </Box>

          <Stack direction="row" spacing={{ xs: 1.5, sm: 2.5 }} flexWrap="wrap" useFlexGap rowGap={0.75}>
            {RISK_BANDS.map((band) => (
              <Stack key={band} direction="row" spacing={0.75} alignItems="center">
                <Box
                  sx={{
                    width: 13,
                    height: 13,
                    borderRadius: 0.5,
                    bgcolor: BAND_COLORS[band].fill,
                    flexShrink: 0,
                  }}
                />
                <Typography sx={{ fontSize: 12, color: "rgba(226,232,240,0.75)" }}>{BAND_LABELS[band]}</Typography>
              </Stack>
            ))}
          </Stack>

          {unplacedRisks.length > 0 && (
            <Typography sx={{ fontFamily: MONO, fontSize: 11.5, color: "rgba(226,232,240,0.5)" }}>
              {unplacedRisks.length} risk{unplacedRisks.length === 1 ? "" : "s"} not yet placed -- drag from the register above.
            </Typography>
          )}
        </Stack>
      )}

      {!loading && <MitigationTable risks={risks} />}
    </Stack>
  );
}
